import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Separator } from 'app/components/ui/separator';
import {
  Bot,
  Key,
  Settings,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Info,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface OpenAISettingsProps {
  form: UseFormReturn<any>;
}

const openaiModels = [
  { id: 'gpt-4o', name: 'GPT-4o (Latest)', description: 'Most capable model' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fast and efficient' },
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    description: 'Balanced performance',
  },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', description: 'Cost-effective' },
  { id: 'gpt-4', name: 'GPT-4', description: 'High accuracy' },
];

const logLevels = [
  { value: 'none', label: 'None' },
  { value: 'error', label: 'Error Only' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
  { value: 'debug', label: 'Debug' },
];

// Helper function to create payload with is_public: 0 for all keys
const createOpenAISettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all OpenAI settings (sensitive data)
  const settingsKeys = [
    'openai_enabled',
    'openai_api_key',
    'openai_organization_id',
    'openai_model',
    'openai_max_tokens',
    'openai_temperature',
    'openai_timeout',
    'openai_retry_attempts',
    'openai_retry_delay',
    'openai_log_level',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function OpenAISettings({ form }: OpenAISettingsProps) {
  const [showApiKey, setShowApiKey] = React.useState(false);
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createOpenAISettingsPayload(formData);

      console.log('OpenAI settings payload with is_public: 0:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('OpenAI settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating OpenAI settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      {/* OpenAI Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5" />
            OpenAI Configuration
          </CardTitle>
          <CardDescription>
            Configure OpenAI API settings for AI-powered features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="openai_enabled"
              checked={form.watch('openai_enabled')}
              onCheckedChange={value => form.setValue('openai_enabled', value)}
            />
            <Label htmlFor="openai_enabled" className="text-base font-medium">
              Enable OpenAI Integration
            </Label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="openai_api_key">API Key</Label>
              <div className="relative">
                <Input
                  id="openai_api_key"
                  {...form.register('openai_api_key')}
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="sk-..."
                  value={form.watch('openai_api_key') || ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-6 w-6 p-0"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Your OpenAI API key from OpenAI dashboard
                </p>
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                >
                  Get Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_organization_id">Organization ID</Label>
              <Input
                id="openai_organization_id"
                {...form.register('openai_organization_id')}
                placeholder="org-..."
                value={form.watch('openai_organization_id') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Optional: Your OpenAI organization ID
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  How to get your API key:
                </p>
                <ol className="text-sm text-blue-700 space-y-1">
                  <li>
                    1. Go to{' '}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      OpenAI Platform
                    </a>
                  </li>
                  <li>2. Sign in to your account</li>
                  <li>3. Click "Create new secret key"</li>
                  <li>4. Copy the generated key (starts with sk-)</li>
                  <li>5. Paste it in the API Key field above</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Model Configuration
          </CardTitle>
          <CardDescription>
            Configure which AI model to use and its parameters.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="openai_model">Model</Label>
              <Select
                value={form.watch('openai_model')}
                onValueChange={value => form.setValue('openai_model', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {openaiModels.map(model => (
                    <SelectItem key={model.id} value={model.id}>
                      <div>
                        <div className="font-medium">{model.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {model.description}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_max_tokens">Max Tokens</Label>
              <Input
                id="openai_max_tokens"
                {...form.register('openai_max_tokens')}
                placeholder="1000"
                type="number"
                min="1"
                max="4000"
                value={form.watch('openai_max_tokens') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Maximum tokens in response (1-4000)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_temperature">Temperature</Label>
              <Input
                id="openai_temperature"
                {...form.register('openai_temperature')}
                placeholder="0.7"
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={form.watch('openai_temperature') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Controls randomness (0 = focused, 2 = creative)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Advanced Settings
          </CardTitle>
          <CardDescription>
            Configure timeout, retry, and logging settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="openai_timeout">Timeout (ms)</Label>
              <Input
                id="openai_timeout"
                {...form.register('openai_timeout')}
                placeholder="30000"
                type="number"
                min="1000"
                max="120000"
                value={form.watch('openai_timeout') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Request timeout in milliseconds
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_retry_attempts">Retry Attempts</Label>
              <Input
                id="openai_retry_attempts"
                {...form.register('openai_retry_attempts')}
                placeholder="3"
                type="number"
                min="0"
                max="10"
                value={form.watch('openai_retry_attempts') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Number of retry attempts on failure
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_retry_delay">Retry Delay (ms)</Label>
              <Input
                id="openai_retry_delay"
                {...form.register('openai_retry_delay')}
                placeholder="1000"
                type="number"
                min="100"
                max="10000"
                value={form.watch('openai_retry_delay') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Delay between retry attempts
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="openai_log_level">Log Level</Label>
              <Select
                value={form.watch('openai_log_level')}
                onValueChange={value =>
                  form.setValue('openai_log_level', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select log level" />
                </SelectTrigger>
                <SelectContent>
                  {logLevels.map(level => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testing & Monitoring */}
      <Card className="hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Testing & Monitoring
          </CardTitle>
          <CardDescription>
            Test your OpenAI configuration and monitor usage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <Label>API Operations</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <CheckCircle className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
              <Button variant="outline" size="sm">
                <Bot className="w-4 h-4 mr-2" />
                Test Chat
              </Button>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Check Usage
              </Button>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-800 font-medium">
                  OpenAI Integration Benefits
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• AI-powered content generation</li>
                  <li>• Smart customer support</li>
                  <li>• Automated responses</li>
                  <li>• Content optimization</li>
                  <li>• Code assistance</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Security Notice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-sm text-red-800 font-medium">
                  Important Security Information
                </p>
                <ul className="text-sm text-red-700 mt-2 space-y-1">
                  <li>
                    • Keep your API key secure and never expose it in
                    client-side code
                  </li>
                  <li>• Use environment variables for API key storage</li>
                  <li>• Regularly rotate your API keys</li>
                  <li>• Monitor API usage to control costs</li>
                  <li>• Be aware of OpenAI's rate limits and pricing</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  OpenAI settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save OpenAI settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
